import React, { useEffect } from 'react';
import { X } from 'lucide-react';

interface ApiLimitPopupProps {
  isOpen: boolean;
  onClose: () => void;
  apiType?: string;
  currentUsage?: number;
  limit?: number;
}

const ApiLimitPopup: React.FC<ApiLimitPopupProps> = ({
  isOpen,
  onClose,
  apiType,
  currentUsage,
  limit
}) => {
  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      // Store original styles
      const originalStyle = window.getComputedStyle(document.body).overflow;
      const originalPosition = window.getComputedStyle(document.body).position;
      
      // Apply multiple scroll lock techniques
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.height = '100%';
      document.documentElement.style.overflow = 'hidden';
      
      // Also add CSS class as backup
      document.body.classList.add('modal-open');
      document.documentElement.classList.add('modal-open');
      
      // Store original values for cleanup
      document.body.setAttribute('data-original-overflow', originalStyle);
      document.body.setAttribute('data-original-position', originalPosition);
    } else {
      // Restore original styles
      const originalOverflow = document.body.getAttribute('data-original-overflow') || 'unset';
      const originalPosition = document.body.getAttribute('data-original-position') || 'static';
      
      document.body.style.overflow = originalOverflow;
      document.body.style.position = originalPosition;
      document.body.style.width = '';
      document.body.style.height = '';
      document.documentElement.style.overflow = '';
      
      // Remove CSS classes
      document.body.classList.remove('modal-open');
      document.documentElement.classList.remove('modal-open');
      
      // Clean up attributes
      document.body.removeAttribute('data-original-overflow');
      document.body.removeAttribute('data-original-position');
    }

    // Cleanup function to restore scroll when component unmounts
    return () => {
      document.body.style.overflow = 'unset';
      document.body.style.position = 'static';
      document.body.style.width = '';
      document.body.style.height = '';
      document.documentElement.style.overflow = '';
      document.body.classList.remove('modal-open');
      document.documentElement.classList.remove('modal-open');
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999999]">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6 relative">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X size={20} />
        </button>

        {/* Icon */}
        <div className="flex justify-center mb-4">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
            <svg
              className="w-8 h-8 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
        </div>

        {/* Title */}
        <h2 className="text-xl font-bold text-gray-900 text-center mb-2">
          API Limit Reached
        </h2>

        {/* Message */}
        <p className="text-gray-600 text-center mb-6">
          API limit reached. Please come back tomorrow for new usage limits.
        </p>

        {/* Reset information */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <h3 className="font-semibold text-blue-900 mb-2">When do limits reset?</h3>
          <p className="text-sm text-blue-800">
            Your API usage limits reset every day at midnight. You'll be able to use all our features again tomorrow!
          </p>
        </div>

        {/* Action button */}
        <button
          onClick={onClose}
          className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          I Understand
        </button>

        {/* Additional help */}
        <p className="text-xs text-gray-500 text-center mt-4">
          Need higher limits? Contact us about our premium plans.
        </p>
      </div>
    </div>
  );
};

export default ApiLimitPopup; 